<template>
  <form method="POST" @submit.prevent="validate" class="auth-login">
    <h1 class="auth-login__title">{{ $t("auth.login.title") }}</h1>
    <div class="auth-login__email-box">
      <InputLabel>
        <template #label>
          {{ $t("common.email") }}
        </template>

        <input
          type="email"
          placeholder="name@example.ru"
          v-model="email"
          name="email"
          v-validate="'required|email'"
          :class="{ 'auth-login__shake': error }"
        />

        <template slot="error" v-if="errors.has('email')">
          {{ $t("auth.login.error.email") }}
        </template>

        <template slot="error" v-if="!!error">
          {{ error }}
        </template>
      </InputLabel>
    </div>

    <div class="auth-login__password-box">
      <InputLabel error-position-static>
        <template #label>
          {{ $t("common.password") }}
        </template>

        <input
          type="password"
          v-model="password"
          name="password"
          v-validate="'required'"
          :class="{ 'auth-login__shake': error }"
        />

        <template slot="error">
          {{ errors.first("password") }}
        </template>
      </InputLabel>
    </div>

    <div class="auth-login__recover-password">
      <router-link :to="{ name: 'password-recovery' }">
        {{ $t("auth.login.forgotPassword") }}
      </router-link>
    </div>

    <PrimaryButton type="submit" class="auth-login__submit">
      {{ $t("auth.login.submit") }}
    </PrimaryButton>

    <div class="auth-login__helper">
      <tooltip clickable>
        <span class="auth-login__helper-text"
          >{{ $t("auth.login.helper") }}
        </span>
        <template #content>
          <p class="description">{{ $t("auth.login.helperText") }}</p>
        </template>
      </tooltip>
    </div>
  </form>
</template>

<script>
import apiUser from "@/api/user";
import getError from "@/helpers/error";
import { PrimaryButton, Tooltip, InputLabel } from "@v2/shared/ui/components";

export default {
  name: "AuthLogin",
  components: { InputLabel, PrimaryButton, Tooltip },

  data() {
    return {
      email: "",
      password: "",
      error: ""
    };
  },

  methods: {
    applyUserError(error) {
      this.password = "";
      this.$validator.reset();

      this.$nextTick(() => {
        this.error = error;
      });
    },

    validate() {
      this.$validator.validate().then(isValid => {
        if (!isValid) {
          return;
        }

        this.submit();
      });
    },

    submit() {
      apiUser
        .authorize(this.email, this.password)
        .then(({ data, token }) => {
          this.$store.commit("auth/LOGIN", { data, token });
          if (window.Echo)
            window.Echo.connector.options.auth.headers["Authorization"] =
              "Bearer " + token;

          this.$router.push({ name: "home" });
        })
        .catch(response => {
          const responseError = getError(response);
          this.applyUserError(responseError);
        });
    }
  },

  watch: {
    email() {
      this.error = "";
    },

    password() {
      this.error = "";
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.auth-login {
  padding: 48px 80px;
  width: 460px;
  box-sizing: border-box;

  @include respond-down(md) {
    padding: 24px;

    width: calc(100vw - 16px * 2);
    max-width: 375px;
    min-width: 288px;
  }

  &__title {
    @include h1();
    margin: 0;
    padding-bottom: 43px;

    @include respond-down(md) {
      font-size: 24px;
    }
  }
  &__helper {
    margin-top: 50px;
    text-align: center;
    position: relative;
    &-text {
      @include hyperlink();
      cursor: pointer;
    }
  }
  &__noacc {
    margin: 0;

    padding-top: 17px;
    padding-bottom: 43px;

    @include respond-down(md) {
      padding-bottom: 24px;
    }

    a {
      font-size: 14px;
      font-weight: 500;
      line-height: 1.5;
      color: #009ee3;
      text-decoration: none;
    }
  }

  &__email-box {
    margin-bottom: 33px;

    input {
      transition: border-color 0.3s;
    }
  }

  &__password-box {
    margin-bottom: 10px;

    @include respond-down(md) {
      margin-bottom: 24px;
    }
  }

  &__recover-password {
    a {
      @include hyperlink();
    }
  }

  &__submit {
    margin-top: 25px;
    width: 100%;

    @include respond-down(md) {
      margin-top: 24px;
    }
  }

  &__shake {
    position: relative;

    border-color: red !important;

    animation: shakeAnimation 0.5s cubic-bezier(0, 1.8, 0.5, 1.8) 0s 2;
  }

  @keyframes shakeAnimation {
    0% {
      left: 0;
    }
    20% {
      left: 8px;
    }
    40% {
      left: -4px;
    }
    60% {
      left: 4px;
    }
    100% {
      left: 0;
    }
  }
}
.description {
  @include description();
}
</style>
